import React from 'react';

// Shared Helper for Section rendering
const DynamicSection = ({ sectionId, data }) => {
    // Experience
    if (sectionId === 'experience' && data.experience.length > 0) {
        return (
            <div className="mb-6">
                <h3 className="section-header">Experience</h3>
                {data.experience.map((exp) => (
                    <div key={exp.id} className="mb-4">
                        <div className="flex justify-between items-baseline mb-1">
                            <h4 className="font-bold text-md">{exp.role}</h4>
                            <span className="text-sm opacity-75">{exp.startDate} - {exp.endDate}</span>
                        </div>
                        <p className="font-medium text-sm mb-1 opacity-90">{exp.company}</p>
                        <p className="text-sm opacity-80 whitespace-pre-wrap">{exp.description}</p>
                    </div>
                ))}
            </div>
        );
    }
    // Education
    if (sectionId === 'education' && data.education.length > 0) {
        return (
            <div className="mb-6">
                <h3 className="section-header">Education</h3>
                {data.education.map((edu) => (
                    <div key={edu.id} className="mb-2">
                        <div className="flex justify-between items-baseline">
                            <h4 className="font-bold text-md">{edu.school}</h4>
                            <span className="text-sm opacity-75">{edu.year}</span>
                        </div>
                        <p className="text-sm opacity-90">{edu.degree}</p>
                    </div>
                ))}
            </div>
        );
    }
    // Skills
    if (sectionId === 'skills' && data.skills.length > 0) {
        return (
            <div className="mb-6">
                <h3 className="section-header">Skills</h3>
                <div className="flex flex-wrap gap-2">
                    {data.skills.map((skill, index) => (
                        <span key={index} className="skill-tag">{skill}</span>
                    ))}
                </div>
            </div>
        );
    }
    // Custom
    const customSection = data.customSections.find(s => s.id === sectionId);
    if (customSection && customSection.items.length > 0) {
        return (
            <div className="mb-6">
                <h3 className="section-header">{customSection.title}</h3>
                {customSection.items.map((item) => (
                    <div key={item.id} className="mb-3">
                        <h4 className="font-bold text-md">{item.name}</h4>
                        {item.link && <p className="text-xs opacity-75">{item.link}</p>}
                        <p className="text-sm opacity-80 whitespace-pre-wrap">{item.description}</p>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

// 4. Modern Template
export const ModernTemplate = ({ data }) => (
    <div className="h-full min-h-[297mm] p-8 font-sans text-gray-800 bg-white">
        <header className="border-b-4 border-teal-500 pb-6 mb-8">
            <h1 className="text-5xl font-extrabold tracking-tighter text-gray-900 mb-2">{data.personalInfo.firstName} {data.personalInfo.lastName}</h1>
            <div className="flex flex-wrap gap-4 text-sm font-medium text-teal-600">
                 <span>{data.personalInfo.email}</span>
                 {data.personalInfo.phone && <span>• {data.personalInfo.phone}</span>}
                 {data.personalInfo.linkedin && <span>• {data.personalInfo.linkedin}</span>}
            </div>
        </header>
        <style>{`
            .section-header { @apply text-teal-700 font-bold uppercase tracking-wider text-sm mb-4 border-b border-gray-200 pb-2; }
            .skill-tag { @apply bg-teal-50 text-teal-800 px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wide; }
        `}</style>
        {data.personalInfo.summary && <div className="mb-8 text-gray-600 leading-relaxed">{data.personalInfo.summary}</div>}
        {data.sectionsOrder.map(id => <DynamicSection key={id} sectionId={id} data={data} />)}
    </div>
);

// 5. Professional Template
export const ProfessionalTemplate = ({ data }) => (
    <div className="h-full min-h-[297mm] p-10 font-sans text-slate-800 bg-[#f8f9fa]">
        <header className="text-center mb-10 pb-10 border-b border-gray-300">
            <h1 className="text-4xl font-serif font-bold text-slate-900 mb-3">{data.personalInfo.firstName} {data.personalInfo.lastName}</h1>
            <div className="text-sm text-slate-500 space-x-3">
                 <span>{data.personalInfo.email}</span>
                 {data.personalInfo.linkedin && <span>| {data.personalInfo.linkedin}</span>}
                 {data.personalInfo.phone && <span>| {data.personalInfo.phone}</span>}
            </div>
        </header>
        <style>{`
            .section-header { @apply text-center font-serif font-bold text-xl text-slate-900 mb-6 italic; }
            .skill-tag { @apply border border-slate-300 px-3 py-1 rounded-full text-xs text-slate-600; }
        `}</style>
        {data.personalInfo.summary && <div className="mb-10 text-center max-w-2xl mx-auto leading-relaxed text-slate-600">{data.personalInfo.summary}</div>}
        {data.sectionsOrder.map(id => <DynamicSection key={id} sectionId={id} data={data} />)}
    </div>
);

// 6. Compact Template
export const CompactTemplate = ({ data }) => (
    <div className="h-full min-h-[297mm] p-6 font-sans text-gray-900 bg-white text-sm leading-snug">
        <header className="flex justify-between items-end border-b-2 border-black pb-2 mb-4">
            <h1 className="text-2xl font-black uppercase">{data.personalInfo.firstName} {data.personalInfo.lastName}</h1>
            <div className="text-right text-xs">
                 <div className="font-bold">{data.personalInfo.email}</div>
                 <div>{data.personalInfo.phone}</div>
            </div>
        </header>
        <style>{`
            .section-header { @apply font-black uppercase text-xs border-b border-black mb-2 mt-4; }
            .skill-tag { @apply inline-block mr-2 mb-1 underline decoration-gray-300 decoration-2 underline-offset-2; }
        `}</style>
         {data.personalInfo.summary && <div className="mb-4 text-xs font-medium">{data.personalInfo.summary}</div>}
        <div className="grid grid-cols-2 gap-x-6">
             <div className="col-span-2">
                 {data.sectionsOrder.map(id => <DynamicSection key={id} sectionId={id} data={data} />)}
             </div>
        </div>
    </div>
);

// 7. Tech Template (Dark Theme)
export const TechTemplate = ({ data }) => (
    <div className="h-full min-h-[297mm] p-8 font-mono text-green-400 bg-gray-900 selection:bg-green-900 selection:text-white">
        <header className="mb-8 border-b border-green-800 pb-6">
            <h1 className="text-3xl font-bold mb-2 prompt">~/{data.personalInfo.firstName}_{data.personalInfo.lastName}</h1>
            <div className="text-xs opacity-75">
                 <span>&gt; {data.personalInfo.email}</span><br/>
                 {data.personalInfo.linkedin && <span>&gt; {data.personalInfo.linkedin}</span>}
            </div>
        </header>
        <style>{`
            .section-header { @apply text-green-500 font-bold mb-4 before:content-['#_'] opacity-90; }
            .skill-tag { @apply bg-green-900/50 text-green-300 px-2 py-0.5 rounded text-xs border border-green-800; }
        `}</style>
        {data.personalInfo.summary && <div className="mb-8 opacity-80 border-l-2 border-green-800 pl-4">{data.personalInfo.summary}</div>}
        {data.sectionsOrder.map(id => <DynamicSection key={id} sectionId={id} data={data} />)}
    </div>
);

// 8. Elegant Template
export const ElegantTemplate = ({ data }) => (
    <div className="h-full min-h-[297mm] p-12 font-serif text-gray-800 bg-[#fffcf5]">
        <header className="text-center mb-12">
            <h1 className="text-5xl italic font-normal text-amber-900 mb-4">{data.personalInfo.firstName} {data.personalInfo.lastName}</h1>
            <div className="w-16 h-1 bg-amber-200 mx-auto mb-6"></div>
            <div className="text-sm text-amber-900/80 uppercase tracking-widest flex justify-center gap-6">
                 <span>{data.personalInfo.email}</span>
                 {data.personalInfo.phone && <span>{data.personalInfo.phone}</span>}
            </div>
        </header>
        <style>{`
            .section-header { @apply text-center text-amber-900/40 font-sans font-bold uppercase tracking-[0.2em] text-xs mb-8 mt-12; }
            .skill-tag { @apply italic text-amber-900/80; }
        `}</style>
        {data.personalInfo.summary && <div className="mb-8 text-center text-lg italic opacity-90 leading-relaxed font-light">{data.personalInfo.summary}</div>}
        {data.sectionsOrder.map(id => <DynamicSection key={id} sectionId={id} data={data} />)}
    </div>
);

// 9. Bold Template
export const BoldTemplate = ({ data }) => (
    <div className="h-full min-h-[297mm] font-sans text-gray-900 bg-white">
        <header className="bg-black text-white p-12 mb-8">
            <h1 className="text-6xl font-black uppercase leading-none mb-4 tracking-tighter">{data.personalInfo.firstName}<br/><span className="text-yellow-400">{data.personalInfo.lastName}</span></h1>
            <div className="text-lg font-bold">
                 <span className="mr-6 border-b-2 border-yellow-400 pb-1">{data.personalInfo.email}</span>
                 {data.personalInfo.linkedin && <span className="border-b-2 border-yellow-400 pb-1">{data.personalInfo.linkedin}</span>}
            </div>
        </header>
        <div className="px-12 pb-12">
            <style>{`
                .section-header { @apply font-black text-2xl uppercase border-l-8 border-yellow-400 pl-4 mb-6; }
                .skill-tag { @apply bg-black text-white px-4 py-2 font-bold text-sm transform -skew-x-12 inline-block mr-2; }
            `}</style>
            {data.personalInfo.summary && <div className="mb-10 text-xl font-medium leading-normal">{data.personalInfo.summary}</div>}
            {data.sectionsOrder.map(id => <DynamicSection key={id} sectionId={id} data={data} />)}
        </div>
    </div>
);
